
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowLeft, UserCircle, Tag } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { VideoCard } from "@/components/videos/VideoCard";
import { AddVideoDialog } from "@/components/videos/AddVideoDialog";
import { useNavigate, Link } from "react-router-dom";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Video {
  id: string;
  title: string;
  description: string;
  youtube_url: string;
  user_id: string;
  category: string | null;
}

const Videos = () => {
  const { t } = useTranslation('common');
  const [isAddingVideo, setIsAddingVideo] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(t('videos.categoryTypes.all'));
  const { toast } = useToast();
  const navigate = useNavigate();

  const categories = [
    t('videos.categoryTypes.all'),
    t('videos.categoryTypes.driving'),
    t('videos.categoryTypes.ironPlay'),
    t('videos.categoryTypes.putting'),
    t('videos.categoryTypes.bunker'),
    t('videos.categoryTypes.wedges'),
    t('videos.categoryTypes.strategy'),
    t('videos.categoryTypes.mental'),
    t('videos.categoryTypes.rules'),
    t('videos.categoryTypes.other')
  ];

  const { data: videos, refetch } = useQuery({
    queryKey: ["youtube-videos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("youtube_videos")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Video[];
    },
  });

  const handleDelete = async (videoId: string, videoUserId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: t('videos.deleteError'),
        description: t('videos.deleteErrorMessage'),
        variant: "destructive",
      });
      return;
    }

    if (user.id !== videoUserId) {
      toast({
        title: t('videos.deleteError'),
        description: t('videos.deletePermissionError'),
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("youtube_videos")
        .delete()
        .eq("id", videoId);

      if (error) throw error;

      toast({
        title: t('videos.deleteSuccess'),
        description: t('videos.deleteSuccessMessage'),
      });
      refetch();
    } catch (error) {
      toast({
        title: t('videos.deleteError'),
        description: t('videos.deleteGeneralError'),
        variant: "destructive",
      });
    }
  };

  const filteredVideos = videos?.filter(video => 
    selectedCategory === t('videos.categoryTypes.all') || video.category === selectedCategory
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="text-golf-600">
            <Link to="/" className="text-green-600 hover:text-green-700 flex items-center gap-2">
              <UserCircle className="h-6 w-6" />
              <span>{t('videos.backToDashboard')}</span>
            </Link>
          </div>
          <AddVideoDialog 
            isOpen={isAddingVideo}
            onOpenChange={setIsAddingVideo}
            onSuccess={refetch}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-golf-800 mb-6">{t('videos.title')}</h1>
          <p className="text-xl text-golf-600 mb-6">
            {t('videos.subtitle')}
          </p>
          
          <div className="flex justify-center items-center mb-6">
            <Tag className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-lg font-medium text-green-800">{t('videos.categories')}</span>
          </div>
          
          <Tabs 
            defaultValue={t('videos.categoryTypes.all')}
            value={selectedCategory}
            onValueChange={setSelectedCategory}
            className="w-full mb-6"
          >
            <TabsList className="flex flex-wrap justify-center max-w-3xl mx-auto">
              {categories.map((category) => (
                <TabsTrigger key={category} value={category} className="px-4 py-2">
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {filteredVideos?.map((video) => (
            <motion.div
              key={video.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <VideoCard
                id={video.id}
                title={video.title}
                description={video.description}
                youtubeUrl={video.youtube_url}
                userId={video.user_id}
                category={video.category}
                onDelete={handleDelete}
                onCategoryUpdate={refetch}
              />
            </motion.div>
          ))}
          
          {filteredVideos?.length === 0 && (
            <div className="col-span-2 text-center py-8">
              <p className="text-lg text-gray-500">
                {t('videos.noVideosFound')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Videos;
